import React, { useMemo, useState } from 'react';
import { DownloadUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Flex,
  message,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';

import type {
  RawScorecardData,
  ScorecardMetricRow,
  ScorecardSummary,
} from '../../types';
import {
  calculateRelativeUplift,
  formatFullDate,
  formatPValue,
  formatRelativeDelta,
  getMetricScorecardErrorMessage,
  ScorecardStatusValue,
} from '../../utils';
import DeepDives from '../DeepDives';
import SqlPreviewModal from '../SqlPreviewModal';
import { axaScorecardEvents } from '@/common/Scribe/EventsList';
import type {
  ExperimentAnalysisConfig,
  MetricWithDimensionFields,
} from '@/features/experiment/constants/analysis';
import { useGetMetricScorecard } from '@/hooks/usePlannerQueries';
import Service from '@/lib/App';

interface Props {
  experimentName: string;
  record: ScorecardMetricRow;
  analysisConfig?: ExperimentAnalysisConfig;
}

interface DimensionFilterValue {
  name: string;
  operator: string;
  values: string[];
}

interface ScorecardTableRow {
  key: string;
  treatmentId: number;
  analysisUnit: string;
  dimensionFilter: DimensionFilterValue[] | null;
  controlMean?: number;
  treatmentMean?: number;
  pValue: number | null;
  uplift: number | null;
  ciLower?: number;
  ciUpper?: number;
}

const MAX_VISIBLE_VALUES = 3;

const findMetricConfigById = (
  analysisConfig: ExperimentAnalysisConfig | undefined,
  metricId: string,
): MetricWithDimensionFields | undefined => {
  if (!analysisConfig) return undefined;

  const allMetrics = [
    ...(analysisConfig.primary_metrics || []),
    ...(analysisConfig.secondary_metrics || []),
    ...(analysisConfig.guardrail_metrics || []),
  ];

  return allMetrics.find((metric) => metric.metric_id === metricId);
};

const getOperatorSymbol = (operator: string) => {
  switch (operator?.toLowerCase()) {
    case 'in':
      return '∈';
    case 'not in':
      return '∉';
    default:
      return operator || '∈';
  }
};

const DimensionFilterCell: React.FC<{
  dimensions: DimensionFilterValue[] | null;
}> = ({ dimensions }) => {
  if (!dimensions || dimensions.length === 0) return <>{'—'}</>;

  return (
    <Flex vertical gap={4}>
      {dimensions.map(({ name, operator, values }) => {
        const unique = Array.from(new Set(values));
        const visible = unique.slice(0, MAX_VISIBLE_VALUES);
        const hiddenCount = unique.length - visible.length;
        const label = `${name} ${getOperatorSymbol(operator)} (${visible.join(', ')}${
          hiddenCount > 0 ? ` +${hiddenCount} more` : ''
        })`;
        const tooltipTitle =
          hiddenCount > 0
            ? `${name} ${getOperatorSymbol(operator)} (${unique.join(', ')})`
            : undefined;

        return (
          <span key={name}>
            <Tooltip title={tooltipTitle}>
              <Tag color="blue" style={{ margin: 0 }}>
                {label}
              </Tag>
            </Tooltip>
          </span>
        );
      })}
    </Flex>
  );
};

const ExpandedRow: React.FC<Props> = ({
  experimentName,
  record,
  analysisConfig,
}) => {
  const { data, isLoading, isError, error } = useGetMetricScorecard(
    experimentName,
    record.metricId,
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleDownloadQuery = async () => {
    const event = axaScorecardEvents.clickDownloadQuery;
    scribe.sendEvent(event.name, {
      STATE_NAME: event.state,
      experiment_name: experimentName,
      metric_id: record.metricId,
      metric_name: record.metricDisplayName,
      metric_level: record.metricLevel,
      metric_status: record.status,
    });

    setIsDownloading(true);
    try {
      await Service.planner.downloadMetricQuery(
        experimentName,
        record.metricId,
      );
    } catch {
      message.error('Failed to download query. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const metricConfig = useMemo(
    () => findMetricConfigById(analysisConfig, record.metricId),
    [analysisConfig, record.metricId],
  );

  const dataSource: ScorecardTableRow[] = useMemo(() => {
    if (!data || !data.scorecard) return [];

    return data.scorecard.map((scorecard: ScorecardSummary) => {
      const raw = scorecard.raw as RawScorecardData | null;
      const dataInfo = raw?.data_info;
      const controlMean =
        dataInfo?.treatment_0?.mean_w ?? dataInfo?.treatment_0?.mean;
      const treatmentData = dataInfo?.treatment_1;

      return {
        key: `treatment-${scorecard.treatmentId}`,
        treatmentId: scorecard.treatmentId,
        analysisUnit:
          metricConfig?.analysis_dimension ?? dataInfo?.id_name ?? '—',
        dimensionFilter:
          metricConfig?.dimension_filters?.flatMap((dimensionFilter) =>
            dimensionFilter.dimension_filter_constraints.map((constraint) => ({
              name: constraint.dimension_name,
              operator: constraint.operator,
              values: Array.isArray(constraint.values)
                ? constraint.values.map(String)
                : [String(constraint.values)],
            })),
          ) ?? null,
        controlMean,
        treatmentMean: treatmentData?.mean_w ?? treatmentData?.mean,
        pValue: scorecard.pValue ?? null,
        uplift: scorecard.uplift ?? null,
        ciLower: scorecard.confidenceInterval?.[0]?.value,
        ciUpper: scorecard.confidenceInterval?.[1]?.value,
      };
    });
  }, [data, metricConfig]);

  const columns: TableColumnsType<ScorecardTableRow> = useMemo(() => {
    return [
      {
        title: 'Treatment ID',
        dataIndex: 'treatmentId',
        key: 'treatmentId',
        width: 100,
        render: (id: number) => `Treatment ${id}`,
      },
      {
        title: 'Analysis Unit',
        dataIndex: 'analysisUnit',
        key: 'analysisUnit',
        width: 120,
      },
      {
        title: 'Dimension Filter',
        dataIndex: 'dimensionFilter',
        key: 'dimensionFilter',
        width: 250,
        render: (filter: DimensionFilterValue[] | null) => (
          <DimensionFilterCell dimensions={filter} />
        ),
      },
      {
        title: 'Control (Mean)',
        dataIndex: 'controlMean',
        key: 'controlMean',
        width: 110,
        align: 'right',
        render: (value: number | undefined) => value?.toFixed(4) ?? '—',
      },
      {
        title: 'Treatment (Mean)',
        dataIndex: 'treatmentMean',
        key: 'treatmentMean',
        width: 120,
        align: 'right',
        render: (value: number | undefined) => value?.toFixed(4) ?? '—',
      },
      {
        title: 'p-value',
        dataIndex: 'pValue',
        key: 'pValue',
        width: 70,
        align: 'right',
        render: (value: number | null) => formatPValue(value),
      },
      {
        title: 'Uplift',
        key: 'uplift',
        width: 60,
        align: 'right',
        render: (_: unknown, record: ScorecardTableRow) =>
          formatRelativeDelta(
            calculateRelativeUplift(record.controlMean, record.treatmentMean),
          ),
      },
      {
        title: 'CI (L)',
        dataIndex: 'ciLower',
        key: 'ciLower',
        width: 60,
        align: 'right',
        render: (value: number | undefined) => value?.toFixed(4) ?? '—',
      },
      {
        title: 'CI (U)',
        dataIndex: 'ciUpper',
        key: 'ciUpper',
        width: 60,
        align: 'right',
        render: (value: number | undefined) => value?.toFixed(4) ?? '—',
      },
    ];
  }, []);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" gap={8} style={{ padding: 24 }}>
        <Spin size="small" />
        <Typography.Text type="secondary">Loading…</Typography.Text>
      </Flex>
    );
  }

  if (isError || !data) {
    return (
      <Flex align="center" justify="center" style={{ padding: 24 }}>
        <Typography.Text type="secondary">
          {getMetricScorecardErrorMessage(error)}
        </Typography.Text>
      </Flex>
    );
  }

  const isStale = data.isPreviousRun;
  const isQueued =
    record.status === ScorecardStatusValue.MIDAS_QUEUED ||
    record.status === ScorecardStatusValue.SCORECARD_PROCESSING;
  const isFailed =
    record.status === ScorecardStatusValue.MIDAS_FAILED ||
    record.status === ScorecardStatusValue.SCORECARD_FAILED;

  return (
    <div
      style={{
        padding: '16px 20px 20px',
        background: '#fafafa',
        borderTop: '1px solid #f0f0f0',
      }}
    >
      {isStale && isQueued && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <>
              <strong>
                Showing last successful scorecard from{' '}
                {formatFullDate(data.runDate)}.
              </strong>{' '}
              Upstream data for the latest run has not arrived yet — values
              below are stale but valid for that date. See{' '}
              <strong>Status</strong> in the row above.
            </>
          }
        />
      )}

      {isStale && isFailed && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <>
              <strong>
                Showing last successful scorecard from{' '}
                {formatFullDate(data.runDate)}.
              </strong>{' '}
              New computation failed — values below are stale but valid for that
              date. See <strong>Failure reasons</strong> in the row above.
            </>
          }
        />
      )}

      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Typography.Text strong>
          Scorecard values — {record.metricDisplayName}
        </Typography.Text>
        <Flex gap={8}>
          <Button size="small" onClick={() => setIsPreviewOpen(true)}>
            Preview SQL
          </Button>
          <Button
            icon={<DownloadUniversal />}
            size="small"
            loading={isDownloading}
            onClick={handleDownloadQuery}
          >
            Download SQL
          </Button>
        </Flex>
      </Flex>

      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
      />

      <DeepDives deepDives={data.deepDives} />

      <SqlPreviewModal
        experimentName={experimentName}
        metricId={record.metricId}
        metricDisplayName={record.metricDisplayName}
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </div>
  );
};

export default ExpandedRow;
